/**
 * LegalDoc.jsx — Shared presentational layout for static legal pages
 * (/privacy, /terms). Renders a structured `doc` object with consistent,
 * readable typography that matches the app's UI (CreditsPage conventions:
 * max-w-3xl, bg-background, ghost back button, var(--font-heading),
 * muted-foreground body, card-style contact block).
 *
 * Web-only pages — not mounted in the mobile app.
 */
import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';

const EMAIL = 'contact@gazumbagames.com';

// Linkify our contact email wherever it appears inside a paragraph string.
function renderText(text) {
  if (typeof text !== 'string' || !text.includes(EMAIL)) return text;
  const parts = text.split(EMAIL);
  return parts.flatMap((part, i) =>
    i < parts.length - 1
      ? [
          part,
          <a
            key={`mail-${i}`}
            href={`mailto:${EMAIL}`}
            className="underline hover:text-foreground"
          >
            {EMAIL}
          </a>,
        ]
      : [part],
  );
}

function Bullet({ item }) {
  if (item && typeof item === 'object' && item.label) {
    return (
      <li>
        <span className="font-medium text-foreground">{item.label}</span>{' '}
        {renderText(item.text)}
      </li>
    );
  }
  return <li>{renderText(item)}</li>;
}

function Section({ section }) {
  return (
    <section className="mt-8">
      <h2
        className="text-lg font-semibold text-foreground mb-3"
        style={{ fontFamily: 'var(--font-heading)' }}
      >
        {section.heading}
      </h2>
      {(section.paragraphs || []).map((p, i) => (
        <p
          key={i}
          className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-3"
        >
          {renderText(p)}
        </p>
      ))}
      {section.bullets && (
        <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-muted-foreground mb-3">
          {section.bullets.map((b, i) => (
            <Bullet key={i} item={b} />
          ))}
        </ul>
      )}
    </section>
  );
}

export default function LegalDoc({ doc, testId, otherLink }) {
  // Static legal pages should be indexable & start at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid={testId}>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            // Full-page nav (not client-side) so leaving a publicly-bypassed
            // legal page re-triggers the site gate on the destination.
            window.location.href = '/';
          }}
          className="mb-4"
          data-testid={`${testId}-back-btn`}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>

        <header className="mb-2">
          <h1
            className="text-2xl sm:text-3xl font-bold"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {doc.title}
          </h1>
          {doc.effectiveDate && (
            <p className="text-sm text-muted-foreground mt-2">
              Effective Date: {doc.effectiveDate}
            </p>
          )}
        </header>

        {(doc.intro || []).map((p, i) => (
          <p
            key={i}
            className="text-sm sm:text-base text-muted-foreground leading-relaxed mt-3"
          >
            {renderText(p)}
          </p>
        ))}

        <article>
          {doc.sections.map((s, i) => (
            <Section key={i} section={s} />
          ))}
        </article>

        {/* Contact block */}
        {doc.contact && (
          <section className="mt-8">
            <h2
              className="text-lg font-semibold text-foreground mb-3"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Contact Us
            </h2>
            <div className="bg-card border border-border/60 rounded-xl p-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
              {doc.contact.lines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
              <div className="mt-1">
                Email:{' '}
                <a
                  href={`mailto:${EMAIL}`}
                  className="underline hover:text-foreground"
                >
                  {EMAIL}
                </a>
              </div>
            </div>
          </section>
        )}

        <footer className="mt-8 pt-6 border-t border-border/60">
          {doc.lastUpdated && (
            <p className="text-xs text-muted-foreground">
              {doc.lastUpdated}
            </p>
          )}
          {otherLink && (
            <p className="text-xs text-muted-foreground mt-2">
              See also:{' '}
              <a
                href={otherLink.href}
                className="underline hover:text-foreground"
                data-testid={`${testId}-other-link`}
              >
                {otherLink.label}
              </a>
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Print-friendly version (no JavaScript required):{' '}
            <a
              href={testId === 'terms-page' ? '/terms.html' : '/privacy.html'}
              className="underline hover:text-foreground"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testId}-noscript-link`}
            >
              {testId === 'terms-page' ? '/terms.html' : '/privacy.html'}
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
